import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  Box,
  Card,
  IconButton,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core';
import { History } from '@material-ui/icons';

import CardContent from '../../../common/card/CardContent';
import { formatDate } from '../../../common/formatter';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import TaskAuditLogList from '../components/TaskAuditLogList';
import { useGetTaskStatusLogs } from '../Task.hooks';
import { Task, TaskStatusLog } from '../Task.interface';
import { useGetWorkflowMachineState } from '../Task.machine';
import { parseTaskStatus } from '../task.helper';

const StyledCard = styled(Card)`
  position: relative;
  .title {
    margin-top: 0;
    font-weight: 500;
  }
  .audit-log {
    position: absolute;
    top: 12px;
    right: 12px;
  }
`;

export interface TaskStatusCardProps {
  task: Task;
}

const TaskStatusCard: React.FC<TaskStatusCardProps> = ({ task }) => {
  const {
    taskStatusLogs: delta,
    isLoading,
    getStatusLogs
  } = useGetTaskStatusLogs(task.id || 0);
  const limit = 5;
  const [page, setPage] = useState(0);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const taskRef = useRef(task); // capturing previous task object
  const [taskStatusLogs, setTaskStatusLogs] = useState<TaskStatusLog[]>([]);
  const status: string = useMemo(() => {
    return parseTaskStatus(task.status) || 'N/A';
  }, [task]);
  useEffect(() => {
    if (task !== taskRef.current) {
      // task object changed from previous; likely, status changed
      taskRef.current = task;
      getStatusLogs({ page: 0, limit }); // fetch updated status change
    } else {
      getStatusLogs({ page, limit }); // get next page
    }
  }, [getStatusLogs, page, task]);
  useEffect(() => {
    if (delta && delta.data) {
      setTaskStatusLogs(taskStatusLogs => {
        if (delta.page === 0) taskStatusLogs = [];
        const logs = [
          ...taskStatusLogs,
          ...delta.data.filter(
            newLog =>
              // don't insert log if it already exists; occurs when partial page load
              !taskStatusLogs.find(log => log.id === newLog.id)
          )
        ];
        logs.sort(
          // resort the list after appending statuses
          (a, b) =>
            new Date(b.created).getTime() - new Date(a.created).getTime()
        );
        return logs;
      });
    }
  }, [delta]);

  return (
    <StyledCard>
      <CardContent background='primary'>
        <Typography
          variant='overline'
          style={{ margin: 0, paddingTop: '20px' }}
        >
          Status
        </Typography>
        <Typography variant='h4' className='title'>
          {status}
        </Typography>
        <Tooltip title='Audit Log' placement='left' arrow>
          <IconButton
            className='audit-log'
            size='small'
            color='secondary'
            onClick={() => setShowAuditLog(!showAuditLog)}
          >
            <History />
          </IconButton>
        </Tooltip>
      </CardContent>
      {showAuditLog ? (
        <TaskAuditLogList task={task} />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>History</TableCell>
                {/* <TableCell>Version</TableCell> */}
                <TableCell>Status</TableCell>
                <TableCell>Employee</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={4} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {taskStatusLogs &&
                taskStatusLogs.map((statusLog, index) => (
                  <TableRow key={index}>
                    <TableCell>{formatDate(statusLog.created)}</TableCell>
                    {/* <TableCell>{statusLog.version}</TableCell> */}
                    <TableCell>
                      <TaskStatusLabel task={task} status={statusLog.status} />
                    </TableCell>
                    <TableCell>
                      {statusLog.createdBy.firstName}{' '}
                      {statusLog.createdBy.lastName}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {delta && delta.total > (page + 1) * limit && (
        <Box textAlign='center' padding={2}>
          <Link
            component='button'
            variant='body2'
            onClick={() => setPage(page + 1)}
          >
            Load More
          </Link>
        </Box>
      )}
    </StyledCard>
  );
};

export default TaskStatusCard;
